/**
 * Zustand des DataGrids: Seite, Seitengröße, Sortierung, Filter, Auswahl und Bearbeitung.
 * Ersatz für die Zustandsverwaltung von `SfGrid` (Paging, Sorting, FilterBar, Cell-Selection,
 * Batch-Edit).
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import type { ViewDataSource } from '@/api/viewDataSource';
import type { BaseEntity, ChinaAppSysView } from '@/types/entities';

import { parseDefaultFilter } from './defaultFilter';
import { parseDefaultOrder } from './defaultOrder';
import type {
  FilterOperator,
  FilterValue,
  GridCellRef,
  GridColumn,
  GridSort,
  GridWhere,
} from './types';

/** `<GridPageSettings PageCount="5" PageSizes="50,100,200" PageSize="50">`. */
export const GRID_PAGE_SIZES: readonly number[] = [50, 100, 200];
export const GRID_PAGE_COUNT = 5;
const DEFAULT_PAGE_SIZE = 50;

/** Standardfilter der Ansicht als Filtergruppen je Feld. */
function toWhereMap(view: ChinaAppSysView | null): Map<string, GridWhere> {
  const map = new Map<string, GridWhere>();
  for (const where of parseDefaultFilter(view?.DefaultFilter)) {
    const field = where.predicates[0]?.field;
    if (field === undefined) continue;
    const existing = map.get(field);
    map.set(
      field,
      existing === undefined
        ? where
        : { predicates: [...existing.predicates, ...where.predicates] },
    );
  }
  return map;
}

export interface GridStateOptions<T extends BaseEntity> {
  view: ChinaAppSysView | null;
  columns: GridColumn<T>[];
  dataSource: ViewDataSource<T>;
  onSelectedIdChange: (id: number | null) => void;
}

/**
 * Alle Rückgaben sind Eigenschaften mit Funktionswert (keine Methoden), damit sie ungebunden
 * an `DataGridHandle` und an die Filterzeile weitergereicht werden dürfen.
 */
export interface GridState<T> {
  rows: T[];
  totalCount: number;
  loading: boolean;
  /** `false`, solange die erste Antwort der Datenquelle aussteht. */
  loaded: boolean;
  page: number;
  pageSize: number;
  sorts: GridSort[];
  selected: GridCellRef | null;
  editing: GridCellRef | null;
  setPage: (page: number) => void;
  setPageSize: (pageSize: number) => void;
  toggleSort: (field: string, additive: boolean) => void;
  selectCell: (rowId: number, field: string) => void;
  startEdit: (rowId: number, field: string) => void;
  stopEdit: () => void;
  clearFiltering: (field?: string) => void;
  filterByColumn: (field: string, operator: FilterOperator, value: FilterValue) => void;
  setColumnWhere: (field: string, where: GridWhere | null) => void;
  refresh: () => Promise<void>;
  setRowData: (id: number, item: T) => void;
  getSelectedRecords: () => T[];
}

export function useGridState<T extends BaseEntity>(options: GridStateOptions<T>): GridState<T> {
  const { columns, dataSource, onSelectedIdChange, view } = options;

  const [rows, setRows] = useState<T[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [sorts, setSorts] = useState<GridSort[]>(() => parseDefaultOrder(view?.DefaultOrder));
  const [wheres, setWheres] = useState<Map<string, GridWhere>>(() => toWhereMap(view));
  const [selected, setSelected] = useState<GridCellRef | null>(null);
  const [editing, setEditing] = useState<GridCellRef | null>(null);

  // Sichert die Reihenfolge der Antworten: ältere Antworten werden verworfen.
  const requestId = useRef(0);

  const refresh = useCallback(async () => {
    requestId.current += 1;
    const current = requestId.current;
    setLoading(true);
    try {
      const result = await dataSource.read({
        skip: (page - 1) * pageSize,
        take: pageSize,
        sorted: sorts,
        where: [...wheres.values()],
      });
      if (current !== requestId.current) return;
      setRows(result.result);
      setTotalCount(result.count);
    } catch {
      // Der Altclient meldet Ladefehler nicht; das Grid bleibt dann leer.
      if (current === requestId.current) {
        setRows([]);
        setTotalCount(0);
      }
    } finally {
      if (current === requestId.current) {
        setLoading(false);
        setLoaded(true);
      }
    }
  }, [dataSource, page, pageSize, sorts, wheres]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Meldet jeden Wechsel des ausgewählten Datensatzes — Pendant zu CellSelected/CellDeselected.
  const notifySelected = useRef(onSelectedIdChange);
  useEffect(() => {
    notifySelected.current = onSelectedIdChange;
  }, [onSelectedIdChange]);

  const notifiedId = useRef<number | null>(null);
  useEffect(() => {
    const id = selected?.rowId ?? null;
    if (notifiedId.current === id) return;
    notifiedId.current = id;
    notifySelected.current(id);
  }, [selected]);

  // Wechselt die Ansicht oder die Spaltenliste, beginnen Seite und Auswahl von vorn.
  const appliedView = useRef(view);
  const appliedColumns = useRef(columns);
  useEffect(() => {
    if (appliedView.current === view && appliedColumns.current === columns) return;
    const viewChanged = appliedView.current !== view;
    appliedView.current = view;
    appliedColumns.current = columns;
    setPage(1);
    setSelected(null);
    setEditing(null);
    if (viewChanged) {
      setSorts(parseDefaultOrder(view?.DefaultOrder));
      setWheres(toWhereMap(view));
    }
  }, [columns, view]);

  const toggleSort = useCallback((field: string, additive: boolean) => {
    setSorts((current) => {
      const existing = current.find((sort) => sort.field === field);
      const others = additive ? current.filter((sort) => sort.field !== field) : [];
      if (existing === undefined) return [...others, { field, direction: 'ascending' }];
      if (existing.direction === 'ascending')
        return [...others, { field, direction: 'descending' }];
      return others;
    });
    setPage(1);
  }, []);

  const clearFiltering = useCallback((field?: string) => {
    setWheres((current) => {
      if (field === undefined) return new Map();
      const next = new Map(current);
      next.delete(field);
      return next;
    });
    setPage(1);
  }, []);

  const filterByColumn = useCallback(
    (field: string, operator: FilterOperator, value: FilterValue) => {
      setWheres((current) =>
        new Map(current).set(field, { predicates: [{ field, operator, value }] }),
      );
      setPage(1);
    },
    [],
  );

  const setColumnWhere = useCallback((field: string, where: GridWhere | null) => {
    setWheres((current) => {
      const next = new Map(current);
      if (where === null) next.delete(field);
      else next.set(field, where);
      return next;
    });
    setPage(1);
  }, []);

  const selectCell = useCallback((rowId: number, field: string) => {
    setSelected((current) =>
      current !== null && current.rowId === rowId && current.field === field
        ? null
        : { rowId, field },
    );
  }, []);

  const startEdit = useCallback((rowId: number, field: string) => {
    setEditing({ rowId, field });
  }, []);

  const stopEdit = useCallback(() => {
    setEditing(null);
  }, []);

  const setRowData = useCallback((id: number, item: T) => {
    setRows((current) => current.map((row) => (row.Id === id ? item : row)));
  }, []);

  const getSelectedRecords = useCallback(
    () => (selected === null ? [] : rows.filter((row) => row.Id === selected.rowId)),
    [rows, selected],
  );

  const changePageSize = useCallback((next: number) => {
    setPageSize(next);
    setPage(1);
  }, []);

  return {
    rows,
    totalCount,
    loading,
    loaded,
    page,
    pageSize,
    sorts,
    selected,
    editing,
    setPage,
    setPageSize: changePageSize,
    toggleSort,
    selectCell,
    startEdit,
    stopEdit,
    clearFiltering,
    filterByColumn,
    setColumnWhere,
    refresh,
    setRowData,
    getSelectedRecords,
  };
}
